import React from 'react';
import { FaTimes as IconClose } from 'react-icons/fa';
import styled from 'styled-components';

import useScreenQuery from '../../../hooks/useScreenQuery';
import PlayupThemeProvider from '../../../themes/PlayupThemeProvider';
import PlayUpLogo from '../../../assets/images/playup-logo.svg';
import DraftstarsLogo from '../../../assets/images/draftstars-logo.png';
import PlusSign from '../../../assets/images/plus-sign-for-brand-logos.svg';

const LoginModalMultiBrands = ({ title, onClose, children }: LoginModalMultiBrandsProps) => {
	const { isSmall } = useScreenQuery();

	const handleKeyDown = React.useCallback(
		(event) => {
			if (onClose && event.key === 'Escape') {
				onClose();
			}
		},
		[onClose]
	);

	React.useEffect(() => {
		window.addEventListener('keydown', handleKeyDown);

		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [handleKeyDown]);

	React.useEffect(() => {
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = 'auto';
		};
	}, []);

	return (
		<PlayupThemeProvider>
			<ModalContext>
				<ModalBackground onClick={onClose} />
				<ModalContainer $isSmallScreen={isSmall} role="dialog" aria-modal="true">
					<ModalHeader $isSmallScreen={isSmall}>
						<IconContainer onClick={onClose}>
							<IconClose size={25} />
						</IconContainer>
					</ModalHeader>
					<BrandLogoContainer $isSmallScreen={isSmall}>
						<BrandLogo src={PlayUpLogo} alt={'PlayUp'} $isSmallScreen={isSmall} />
						<LogoConnector src={PlusSign} alt={'+'} $isSmallScreen={isSmall}></LogoConnector>
						<BrandLogo src={DraftstarsLogo} alt={'Draftstars'} $isSmallScreen={isSmall} />
					</BrandLogoContainer>
					<ModalTitle $isSmallScreen={isSmall}>{title}</ModalTitle>
					<ModalBody $isSmallScreen={isSmall}>{children}</ModalBody>
				</ModalContainer>
			</ModalContext>
		</PlayupThemeProvider>
	);
};

export default LoginModalMultiBrands;

export interface LoginModalMultiBrandsProps {
	title?: string;
	onClose: () => void;
	showFantasyLogo?: boolean;
	isBooki?: boolean;
	children: React.ReactNode;
}

const ModalContext = styled.div`
	display: flex;
	justify-content: center;
	z-index: 500;
`;

const ModalBackground = styled.div`
	position: fixed;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	background-color: ${({ theme }) => theme.colors.black};
	opacity: 0.5;
	z-index: 510;
`;

// z-index: needs to be >399 so it shows over Draftstarts' mobile bottom bar
// if changed, please change the ones in SignupAndLoginModal.tsx as well
const ModalContainer = styled.div<{ $isSmallScreen: boolean }>`
	position: fixed;
	display: flex;
	flex-direction: column;
	align-items: center;
	z-index: 520;
	padding: ${({ $isSmallScreen }) => ($isSmallScreen ? '0 5px 5px' : '0 30px 30px')};
	color: ${({ theme }) => theme.colors.white};
	background-color: ${({ theme }) => theme.colors.darkR};
	width: 100%;
	max-width: 630px;
	max-height: 90%;

	${({ $isSmallScreen }) =>
		$isSmallScreen
			? 'bottom: 0; border-top-left-radius: 5px; border-top-right-radius: 5px;'
			: 'top: 30px; border-radius: 5px;'}
`;

const ModalHeader = styled.div<{ $isSmallScreen: boolean }>`
	display: flex;
	justify-content: right;
	align-items: center;
	width: 100%;
	margin-top: 10px;
`;

const BrandLogoContainer = styled.div<{ $isSmallScreen: boolean }>`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: ${({ $isSmallScreen }) => ($isSmallScreen ? '5px 0 15px' : '10px 0 20px')};
`;

const BrandLogo = styled.img<{ $isSmallScreen: boolean }>`
	height: ${({ $isSmallScreen }) => ($isSmallScreen ? '30px;' : '30px;')};
`;

const LogoConnector = styled.img<{ $isSmallScreen: boolean }>`
	height: ${({ $isSmallScreen }) => ($isSmallScreen ? '30px;' : '30px;')};
	margin-right: 5px;
`;

const ModalTitle = styled.p<{ $isSmallScreen: boolean }>`
	text-align: center;
	font-size: ${({ $isSmallScreen }) => ($isSmallScreen ? '15px' : '14px')};
	font-weight: 700;
	color: ${({ theme }) => theme.colors.white};
	margin-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? '15px' : '20px')};
`;

const ModalBody = styled.div<{ $isSmallScreen: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	padding-top: ${({ $isSmallScreen }) => ($isSmallScreen ? '15px' : '18px')};
	padding-left: 9px;
	padding-right: 9px;
	padding-bottom: ${({ $isSmallScreen }) => ($isSmallScreen ? '57px' : '13px')};
	color: ${({ theme }) => theme.colors.darkR};
	background-color: ${({ theme }) => theme.colors.white};
	width: 100%;
	height: 100%;
	overflow-y: scroll;

	&::-webkit-scrollbar {
		display: none;
	}

	${({ $isSmallScreen }) =>
		$isSmallScreen
			? 'bottom: 0; border-top-left-radius: 5px; border-top-right-radius: 5px;'
			: 'top: 30px; border-radius: 5px;'}
`;

const IconContainer = styled.div`
	color: ${({ theme }) => theme.colors.darkGreyR};
	cursor: pointer;
`;
